import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useConfigurator } from './ConfiguratorProvider';
import { PanelSelection } from './steps/PanelSelection';
import { InverterSelection } from './steps/InverterSelection';
import { RoofTypeSelection } from './steps/RoofTypeSelection';
import { MountingSystemSelection } from './steps/MountingSystemSelection';
import { InstallationMethodSelection } from './steps/InstallationMethodSelection';
import { ConfigurationSummary } from './steps/ConfigurationSummary';

const steps = [
  { title: 'Zonnepanelen', component: PanelSelection },
  { title: 'Omvormer', component: InverterSelection },
  { title: 'Daktype', component: RoofTypeSelection },
  { title: 'Montagesysteem', component: MountingSystemSelection },
  { title: 'Plaatsing', component: InstallationMethodSelection },
  { title: 'Overzicht', component: ConfigurationSummary }
];

export const ConfiguratorModal = () => {
  const { 
    isOpen, 
    setIsOpen, 
    currentStep, 
    setCurrentStep, 
    resetConfiguration,
    configuration 
  } = useConfigurator();

  const canGoNext = () => {
    switch(currentStep) {
      case 0: return !!configuration.panel;
      case 1: return !!configuration.inverter;
      case 2: return !!configuration.roofType;
      case 3: return !!configuration.mountingSystem;
      case 4: return !!configuration.installationMethod;
      default: return true;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    resetConfiguration();
  };

  const CurrentStepComponent = steps[currentStep].component;
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl">
            Zonnepaneel Configurator
          </DialogTitle>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Stap {currentStep + 1} van {steps.length}</span>
            <span>{steps[currentStep].title}</span>
          </div>
          <Progress value={progress} className="w-full" />
        </div>

        {/* Step Content */}
        <div className="min-h-[400px] py-6">
          <CurrentStepComponent />
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 border-t">
          <Button 
            variant="outline" 
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Vorige
          </Button>

          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          <Button 
            onClick={handleNext}
            disabled={!canGoNext() || currentStep === steps.length - 1}
          >
            Volgende
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};